import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Building2,
  User,
  Mail,
  Phone,
  Globe,
  FileText,
  Users,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Shield
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PartnerPortalPage() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    website: "",
    company_type: "",
    num_employees: "",
    country: "Bahamas",
    message: "",
  });

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_name || !form.email) {
      toast.error("Please fill in company name, contact name, and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/partners/inquire`, form);
      setSubmitted(true);
      toast.success("Inquiry submitted!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit. Please try again.");
    }
    setLoading(false);
  };

  const inputClass = "w-full bg-[#0a1628] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-slate-600 focus:border-[#52BFB8]/50 focus:ring-1 focus:ring-[#52BFB8]/30 outline-none transition-colors";

  return (
    <div className="bg-[#0a1628] min-h-screen" style={{ fontFamily: "Inter, sans-serif" }}>
      <div className="max-w-2xl mx-auto px-4 py-10 md:py-16">

        {/* Back link */}
        <Link to="/partners/verify" className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-[#52BFB8] transition-colors mb-6" data-testid="back-link">
          <ArrowLeft className="w-4 h-4" /> Partners
        </Link>

        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-[#52BFB8]/10 border border-[#52BFB8]/20 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-[#52BFB8]" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-white tracking-tight" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>
              Partner Portal
            </h1>
            <p className="text-sm text-slate-400">Tell us about your company to get started</p>
          </div>
        </div>
        <p className="text-xs text-slate-500 mb-8 ml-[52px]">
          Our partnerships team will review your inquiry and reach out within 1–2 business days.
        </p>

        {!submitted ? (
          <form onSubmit={handleSubmit} className="rounded-2xl bg-[#111d32]/50 border border-white/[0.06] p-6 md:p-8" data-testid="partner-form">
            <div className="space-y-4">

              {/* Company name */}
              <div>
                <label className="text-xs text-slate-400 mb-1.5 flex items-center gap-1.5"><Building2 className="w-3 h-3" /> Company Name *</label>
                <input value={form.company_name} onChange={set("company_name")} required className={inputClass} placeholder="e.g. First National Bank" data-testid="input-company-name" />
              </div>

              {/* Contact name + email */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-slate-400 mb-1.5 flex items-center gap-1.5"><User className="w-3 h-3" /> Contact Name *</label>
                  <input value={form.contact_name} onChange={set("contact_name")} required className={inputClass} placeholder="John Rolle" data-testid="input-contact-name" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1.5 flex items-center gap-1.5"><Mail className="w-3 h-3" /> Work Email *</label>
                  <input type="email" value={form.email} onChange={set("email")} required className={inputClass} placeholder="john@company.com" data-testid="input-email" />
                </div>
              </div>

              {/* Phone + website */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-slate-400 mb-1.5 flex items-center gap-1.5"><Phone className="w-3 h-3" /> Phone</label>
                  <input value={form.phone} onChange={set("phone")} className={inputClass} placeholder="(242) 000-0000" data-testid="input-phone" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1.5 flex items-center gap-1.5"><Globe className="w-3 h-3" /> Website</label>
                  <input value={form.website} onChange={set("website")} className={inputClass} placeholder="https://company.com" data-testid="input-website" />
                </div>
              </div>

              {/* Company type + size */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-slate-400 mb-1.5 flex items-center gap-1.5"><FileText className="w-3 h-3" /> Company Type</label>
                  <select value={form.company_type} onChange={set("company_type")} className={inputClass} data-testid="select-company-type">
                    <option value="">Select...</option>
                    <option value="commercial_bank">Commercial Bank</option>
                    <option value="credit_union">Credit Union</option>
                    <option value="mortgage_broker">Mortgage Broker</option>
                    <option value="insurance">Insurance Provider</option>
                    <option value="auto_dealer">Auto Dealer / Finance</option>
                    <option value="real_estate">Real Estate Firm</option>
                    <option value="corporate_services">Corporate Services</option>
                    <option value="fintech">Fintech</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1.5 flex items-center gap-1.5"><Users className="w-3 h-3" /> Company Size</label>
                  <select value={form.num_employees} onChange={set("num_employees")} className={inputClass} data-testid="select-size">
                    <option value="">Select...</option>
                    <option value="1-10">1–10 employees</option>
                    <option value="11-50">11–50 employees</option>
                    <option value="51-200">51–200 employees</option>
                    <option value="201-500">201–500 employees</option>
                    <option value="500+">500+ employees</option>
                  </select>
                </div>
              </div>

              {/* Country */}
              <div>
                <label className="text-xs text-slate-400 mb-1.5 flex items-center gap-1.5"><Globe className="w-3 h-3" /> Country / Region</label>
                <input value={form.country} onChange={set("country")} className={inputClass} placeholder="Bahamas" data-testid="input-country" />
              </div>

              {/* Message */}
              <div>
                <label className="text-xs text-slate-400 mb-1.5 flex items-center gap-1.5"><FileText className="w-3 h-3" /> How can we help?</label>
                <textarea value={form.message} onChange={set("message")} rows={3} className={`${inputClass} resize-none`} placeholder="Tell us about your use case, integration needs, or questions..." data-testid="input-message" />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full mt-6 bg-[#52BFB8] hover:bg-[#3ea8a1] text-[#0a1628] font-semibold py-6 text-base rounded-xl shadow-[0_0_24px_rgba(82,191,184,0.3)] disabled:opacity-50"
              data-testid="submit-inquiry-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Submit to Sales
            </Button>

            <p className="text-[11px] text-slate-600 text-center mt-4">
              Your information is handled securely and will only be used to follow up on your inquiry.
            </p>
          </form>
        ) : (
          <div className="rounded-2xl bg-[#111d32]/50 border border-[#52BFB8]/20 p-8 md:p-10 text-center" data-testid="submission-success">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#52BFB8]/15 flex items-center justify-center mb-5">
              <CheckCircle2 className="w-8 h-8 text-[#52BFB8]" />
            </div>
            <h2 className="text-2xl font-semibold text-white mb-2" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>
              Inquiry Received
            </h2>
            <p className="text-sm text-slate-400 mb-6 max-w-sm mx-auto">
              Thank you, {form.contact_name}. Our partnerships team will review your submission and reach out to <span className="text-[#52BFB8]">{form.email}</span> within 1–2 business days.
            </p>
            <div className="flex gap-3 justify-center">
              <Link to="/partners/verify">
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/5">
                  Back to Partners
                </Button>
              </Link>
              <Link to="/book-demo">
                <Button className="bg-[#52BFB8] hover:bg-[#3ea8a1] text-[#0a1628] font-semibold">
                  Schedule a Demo
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
